/**
 * Cart Service for Gallery Builder
 * Handles all cart-related operations for the gallery builder
 */

export class CartService {
  constructor() {
    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
    this.shopifyRoot = root;
    this.cartEndpoint = `${root}cart/add.js`;
    this.cartDataEndpoint = `${root}cart.js`;

    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);
      this.sectionsContextPath = `${currentUrl.pathname}${currentUrl.search}` || '/';
      currentUrl.searchParams.set('sections', 'ajax-cart');
      this.cartSectionEndpoint = currentUrl.toString();
    } else {
      this.sectionsContextPath = '/';
      this.cartSectionEndpoint = `${root}?sections=ajax-cart`;
    }
  }

  /**
   * Add multiple items to the Shopify cart
   * @param {Array<Object>} items - Array of cart items with variant IDs and properties
   * @param {string|number} items[].id - The variant ID
   * @param {number} items[].quantity - The quantity to add
   * @param {Object} items[].properties - Custom line item properties
   * @returns {Promise<Object>} The cart response from Shopify
   * @throws {Error} If the cart request fails
   */
  async addItemsToCart(items) {
    if (!items || !Array.isArray(items) || items.length === 0) {
      throw new Error('Invalid items array provided');
    }

    try {
      const response = await fetch(this.cartEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items,
          sections: 'ajax-cart',
          sections_url: this.sectionsContextPath
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.description || `Cart error: ${response.status}`;
        const error = new Error(errorMessage);
        error.shopifyError = errorData;
        error.status = response.status;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to add items to cart:', error);
      throw error;
    }
  }

  /**
   * Opens the mini cart drawer
   */
  openMiniCart() {
    let opened = false;

    if (this.hasAlpineCartStore()) {
      try {
        Alpine.store('cart').cartIsOpen = true;
        opened = true;
      } catch (error) {
        console.warn('Failed to open cart via Alpine store:', error);
      }
    }

    if (!opened) {
      const cartContainer = document.querySelector('.ajax-cart__container');
      if (cartContainer) {
        cartContainer.classList.add('ajax-cart__container-visible');
        opened = true;
      }

      const overlay = document.querySelector('.ajax-cart-overlay');
      if (overlay) {
        overlay.style.display = 'block';
      }
    }

    document.dispatchEvent(new CustomEvent('cart:open'));

    return opened;
  }

  /**
   * Refreshes cart data and updates the UI
   * @returns {Promise<Object>} The updated cart object
   */
  async refreshCart() {
    try {
      const response = await fetch(this.cartDataEndpoint);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart data: ${response.status}`);
      }

      const cart = await response.json();

      if (this.hasAlpineCartStore()) {
        this.updateCartStore(cart);
      }

      await this.updateCartSection();

      return cart;
    } catch (error) {
      console.error('Failed to refresh cart:', error);
      throw error;
    }
  }

  /**
   * Updates the cart section HTML with fresh data
   *
   * @private
   * @returns {Promise<void>}
   */
  async updateCartSection() {
    try {
      const response = await fetch(this.cartSectionEndpoint);
      const data = await response.json();

      const sectionHtml = data?.sections?.['ajax-cart'] || data?.['ajax-cart'];
      if (sectionHtml) {
        this.renderCartSection(sectionHtml);
      }
    } catch (error) {
      console.error('Failed to update cart section:', error);
    }
  }

  /**
   * Renders the cart section HTML
   *
   * @private
   * @param {string} html - The HTML string to render
   */
  renderCartSection(html) {
    const cartWrapper = document.querySelector('.cart-inner__wrapper');
    if (!cartWrapper) {
      return;
    }

    const parser = new DOMParser();
    const newDoc = parser.parseFromString(html, 'text/html');
    const incomingWrapper = newDoc.querySelector('.ajax-cart__container .cart-inner__wrapper');

    if (!incomingWrapper) {
      return;
    }

    cartWrapper.innerHTML = incomingWrapper.innerHTML;

    if (window.Alpine) {
      const initTree = window.Alpine.initTree || window.Alpine.initializeTree;
      if (typeof initTree === 'function') {
        const target = cartWrapper.closest('#shopify-section-ajax-cart') || cartWrapper;
        initTree(target);
      }
    }
  }

  /**
   * Updates the Alpine cart store with new data
   * @private
   * @param {Object} cart - The cart object from Shopify
   */
  updateCartStore(cart) {
    try {
      const cartStore = Alpine.store('cart');

      cartStore.cartAllItemsQuantity = cart.item_count;
      cartStore.generalItems = cart.items;
      cartStore.cartTotal = cart.total_price;

      if (cartStore.getCardItems && typeof cartStore.getCardItems === 'function') {
        cartStore.getCardItems();
      }
    } catch (error) {
      console.warn('Failed to update cart store:', error);
    }
  }

  /**
   * Checks if Alpine.js and the cart store are available
   * @private
   * @returns {boolean} True if Alpine and cart store exist
   */
  hasAlpineCartStore() {
    return typeof window.Alpine !== 'undefined' &&
           typeof Alpine.store === 'function';
  }

  /**
   * Formats cart items for display in success messages
   * @param {Array<Object>} items - The items that were added
   * @returns {string} A formatted string describing the items
   */
  formatItemsForMessage(items) {
    const count = items.length;
    const type = items[0]?.properties?._gallery_set || 'items';

    if (count === 1) {
      return '1 artwork';
    } else if (count === 2) {
      return `${type} set (2 artworks)`;
    } else if (count === 3) {
      return `${type} set (3 artworks)`;
    } else {
      return `${count} artworks`;
    }
  }

  /**
   * Ensures the cart store is initialized
   * @returns {Promise<boolean>} True if cart store is ready
   */
  async ensureCartStore() {
    if (this.hasAlpineCartStore() && Alpine.store('cart')) {
      return true;
    }

    if (typeof window.Alpine !== 'undefined') {
      return new Promise((resolve) => {
        if (document.readyState === 'loading') {
          document.addEventListener('alpine:init', () => resolve(true), { once: true });
        } else {
          resolve(true);
        }
      });
    }

    return false;
  }
}

export const cartService = new CartService();
